import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

class InspectionPhotosController extends Controller {
  static targets = [
    'cameraInput', 'cameraForm', 'galleryInput', 'galleryForm', 'spinner',
    'lightbox', 'lightboxImg',
    'deleteBtn', 'deleteActions', 'deleteCount', 'gallery'
  ]

  static values = {
    deleteUrl: String,
    csrfToken: String
  }

  connect () {
    this._deleteMode = false
    this._selectedIds = new Set()
  }

  openCamera () {
    this.cameraInputTarget.click()
  }

  openGallery () {
    this.galleryInputTarget.click()
  }

  submitCamera () {
    if (this.cameraInputTarget.files.length > 0) {
      this.showSpinner()
      this.cameraFormTarget.requestSubmit()
    }
  }

  submitGallery () {
    if (this.galleryInputTarget.files.length > 0) {
      this.showSpinner()
      this.galleryFormTarget.requestSubmit()
    }
  }

  showSpinner () {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove('hidden')
    }
  }

  hideSpinner () {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add('hidden')
    }
  }

  cameraFormTargetConnected (el) {
    el.addEventListener('turbo:submit-end', () => {
      this.hideSpinner()
      this.cameraInputTarget.value = ''
    })
  }

  galleryFormTargetConnected (el) {
    el.addEventListener('turbo:submit-end', () => {
      this.hideSpinner()
      this.galleryInputTarget.value = ''
    })
  }

  // ── Photo click handler ──

  handlePhotoClick (e) {
    if (this._deleteMode) {
      e.preventDefault()
      this.togglePhotoSelection(e)
    } else {
      this.viewPhoto(e)
    }
  }

  // ── Lightbox ──

  viewPhoto (e) {
    e.preventDefault()
    const url = e.currentTarget.dataset.largeUrl
    if (!url || !this.hasLightboxTarget) return

    this.lightboxImgTarget.src = url
    this.lightboxTarget.classList.remove('hidden')
  }

  closeLightbox () {
    if (!this.hasLightboxTarget) return
    this.lightboxTarget.classList.add('hidden')
    this.lightboxImgTarget.src = ''
  }

  stopPropagation (e) {
    e.stopPropagation()
  }

  // ── Delete mode ──

  enterDeleteMode () {
    this._deleteMode = true
    this._selectedIds = new Set()

    // Toggle buttons
    if (this.hasDeleteBtnTarget) this.deleteBtnTarget.classList.add('hidden')
    if (this.hasDeleteActionsTarget) this.deleteActionsTarget.classList.remove('hidden')

    // Show checkboxes on all photos
    this.galleryTarget.querySelectorAll('[data-photo-id]').forEach(el => {
      el.querySelector('[data-role="check"]')?.classList.remove('hidden')
      el.classList.add('cursor-pointer')
      el.querySelector('img')?.classList.remove('cursor-zoom-in')
      el.querySelector('img')?.classList.add('cursor-pointer')
    })

    this.updateDeleteCount()
  }

  exitDeleteMode () {
    this._deleteMode = false
    this._selectedIds = new Set()

    // Toggle buttons
    if (this.hasDeleteBtnTarget) this.deleteBtnTarget.classList.remove('hidden')
    if (this.hasDeleteActionsTarget) this.deleteActionsTarget.classList.add('hidden')

    // Hide checkboxes, remove selection
    this.galleryTarget.querySelectorAll('[data-photo-id]').forEach(el => {
      const check = el.querySelector('[data-role="check"]')
      if (check) {
        check.classList.add('hidden')
        check.classList.remove('bg-primary')
        check.innerHTML = ''
      }
      el.classList.remove('ring-2', 'ring-primary', 'cursor-pointer')
      el.querySelector('img')?.classList.add('cursor-zoom-in')
      el.querySelector('img')?.classList.remove('cursor-pointer')
    })
  }

  togglePhotoSelection (e) {
    const photoEl = e.currentTarget.closest('[data-photo-id]')
    if (!photoEl) return

    const id = photoEl.dataset.photoId
    const check = photoEl.querySelector('[data-role="check"]')

    if (this._selectedIds.has(id)) {
      this._selectedIds.delete(id)
      photoEl.classList.remove('ring-2', 'ring-primary')
      if (check) {
        check.classList.remove('bg-primary')
        check.innerHTML = ''
      }
    } else {
      this._selectedIds.add(id)
      photoEl.classList.add('ring-2', 'ring-primary')
      if (check) {
        check.classList.add('bg-primary')
        check.innerHTML = '<svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>'
      }
    }

    this.updateDeleteCount()
  }

  updateDeleteCount () {
    if (this.hasDeleteCountTarget) {
      const count = this._selectedIds.size
      this.deleteCountTarget.textContent = count > 0 ? ` (${count})` : ''
    }
  }

  async confirmDelete () {
    if (this._selectedIds.size === 0) return

    const ids = [...this._selectedIds]
    const deletedIds = []
    const baseUrl = this.deleteUrlValue

    for (const id of ids) {
      try {
        const response = await fetch(`${baseUrl}/${id}`, {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': this.csrfTokenValue
          }
        })

        if (response.ok || response.status === 204) {
          const el = this.galleryTarget.querySelector(`[data-photo-id="${id}"]`)
          if (el) el.remove()
          deletedIds.push(id)
        }
      } catch (err) {
        console.error('Delete photo error:', err)
      }
    }

    this.exitDeleteMode()

    // Notify body-marks controller about deleted photos
    if (deletedIds.length > 0) {
      document.dispatchEvent(new CustomEvent('photos:deleted', { detail: { ids: deletedIds } }))
    }
  }
}

stimulus.register('inspection-photos', InspectionPhotosController)
